import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Flex,
  Input,
  message,
  Space,
} from "antd";
import { useEffect, useState } from "react";
import { useNotify } from "../../../components/Notification";
import ModalAddPermission from "./components/ModalAddPermission";
import ModelEditPermission from "./components/ModelEditPermission";
import { permissionAPIWithErrorHandling } from "../../../api/admin/permission";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["name"];

const ListPermission = () => {
  const notify = useNotify();
  //State
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [permission, setPermission] = useState();
  const [valueSearch, setValueSearch] = useState("");

  const [listPermission, setListPermission] = useState([]);

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  //table
  const columnDefs = [
    {
      key: "label", label: "Tên permission", fixed: true,
      column: {
        title: "Tên permission",
        dataIndex: "label",
        key: "3",
        fixed: "left",
        width: 350,
        render: (_, record) => {
          const isParent = record.level === 0;

          return (
            <Flex
              align="center"
              gap={10}
              style={{
                paddingLeft: 24 * (record.level ?? 0),
              }}
            >
              {isParent && (
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "#4096ff",
                    display: "inline-block",
                  }}
                />
              )}
              <span
                style={{
                  color: isParent ? "#4096ff" : "inherit",
                  fontWeight: isParent ? "bold" : "normal",
                }}
              >
                {record.label}
              </span>
            </Flex>
          );
        },
      },
    },
    {
      key: "name", label: "Mã permission",
      column: {
        title: "Mã permission",
        dataIndex: "name",
        key: "name",
        width: 350,
        render: (name) => {
          return <p style={{ color: "#4096ff" }}>{name}</p>;
        },
      },
    },
    actionColumnDef(
      (record) => [
        {
          key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit",
          onClick: () => {
            setOpenEdit(true);
            setPermission(record);
          },
        },
        { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => showDeleteConfirm(record) },
      ],
      { width: 100 }
    ),
  ];
  // table

  //function
  const buildVisibleList = (tree, level = 0) => {
    let result = [];
    for (let item of tree) {
      result.push({ ...item, level });
      if (item.children && item.children.length > 0) {
        result = result.concat(buildVisibleList(item.children, level + 1));
      }
    }
    return result;
  };

  const showDeleteConfirm = async (record) => {
    const confirmed = await confirmDelete({ entity: "permission", name: record.name });
    if (!confirmed) return;

    try {
      await permissionAPIWithErrorHandling.deletePermission(record.id);

      notify.success({
        message: "Thành công",
        description: "Bạn đã xóa permission thành công!",
      });

      await fetchActions(); // 👈 Gọi lại sau khi xoá xong
    } catch (error) {
      message.error(error.message || "Xóa permission thất bại");
    }
  };

  const fetchActions = async (search) => {
    try {
      setIsLoading(true);

      const params = {
        search: (search !== '' && search !== undefined) ? search.trim() : null,
      };
      const response = await permissionAPIWithErrorHandling.getListAllPermission(params);

      if (response.data.success && response.data.data) {
        const result = response.data.data;
        setListPermission(buildVisibleList(result) || []);
        message.success("Truy xuất thông tin quyền thành công!")
      }
    } catch (error) {
      notify.error({
        message: "Thất bại",
        description: error?.message || "Lỗi không xác định",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitPermission = async (data) => {
    const request = {
      name: data.name,
      label: data.label,
      parent_id: data.parent_id || null,
    };

    try {
      setIsLoading(true);
      const response = await permissionAPIWithErrorHandling.createPermission(request);

      if (response.data) {
        notify.success({
          message: "Thành công",
          description: "Bạn đã tạo permission thành công!",
        });
        fetchActions();
        setIsLoading(false);
      }

    } catch (error) {
      notify.error({
        message: "Thất bại",
        description: error.message || "Không thể thêm mới quyền!",
      });
      console.error('Error create:', error);
    } finally {
      setIsLoading(false);
      setOpen(false);
    }
  };

  const handleEditPermission = async (data) => {
    try {
      setIsLoading(true);
      const response = await permissionAPIWithErrorHandling.updatePermission(
        permission?.id,
        data
      );

      if (response) {
        notify.success({
          message: "Thành công",
          description: "Cập nhật permission thành công!",
        });
        fetchActions();
      }

    } catch (error) {
      notify.error({
        message: "Thất bại",
        description: error.message || "Không thể sửa permission",
      });
      console.error('Error updating:', error);

    } finally {
      setIsLoading(false);
      setOpenEdit(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };
  const handleCancelEdit = () => {
    setOpenEdit(false);
  };
  const handleSearch = () => {
    fetchActions(valueSearch);
  };

  useEffect(() => {
    if (valueSearch.trim() === "") {
      fetchActions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueSearch]);

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 16, md: 10 },
      field: (
        <Space.Compact style={{ width: "100%" }}>
          <Input
            placeholder="Tìm kiếm..."
            variant="filled"
            value={valueSearch}
            onChange={(e) => setValueSearch(e.target.value)}
            allowClear
          />
          <Button type="primary" onClick={() => handleSearch()}>
            Tìm kiếm
          </Button>
        </Space.Compact>
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!valueSearch;

  return (
    <BrandedPageLayout
      title="Phân quyền"
      subtitle="Quản lý danh sách quyền hệ thống."
      breadcrumbItems={[{ title: "Phân quyền" }]}
    >
      <style>{`
        .custom-table .ant-table-body,
        .custom-table .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
          scrollbar-gutter: stable;
        }
      `}</style>

      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={() => setValueSearch("")}
        onReload={() => fetchActions()}
        extra={
          <Button
            type="primary"
            onClick={() => setOpen(true)}
            icon={<PlusOutlined />}
          >
            Thêm permission
          </Button>
        }
      />

      <ListTableCard
        className="custom-table"
        columns={columns}
        dataSource={listPermission}
        loading={isLoading}
        pagination={false}
        expandIconColumnIndex={-1}
        scroll={{ y: 55 * 10, x: "max-content" }}
        emptyText="Không có dữ liệu"
      />

      {open && (
        <ModalAddPermission
          isOpen={open}
          onOk={(data) => handleSubmitPermission(data)}
          onCancel={handleCancel}
        />
      )}
      {openEdit && (
        <ModelEditPermission
          isOpen={openEdit}
          onOk={(data) => handleEditPermission(data)}
          onCancel={handleCancelEdit}
          permission={permission}
        />
      )}
    </BrandedPageLayout>
  );
};
export default ListPermission;
